import ExcelJS from 'exceljs';
import { aggregateByTypeObject, getRequestGroupLabel } from '../../utils/requestTypeLabels';
import { captureChartBlocks } from '../../utils/captureElement';

const formatDt = (v) => (v ? new Date(v).toLocaleString('ru-RU') : '—');

export function buildWorkSummaryFilename(prefix = 'work_summary') {
  const now = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  const time = `${p(now.getHours())}-${p(now.getMinutes())}-${p(now.getSeconds())}`;
  return `${prefix}_${date}_${time}.xlsx`;
}

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function addJsonSheet(workbook, name, rows) {
  const sheet = workbook.addWorksheet(name);
  if (!rows?.length) {
    sheet.addRow(['Нет данных']);
    return sheet;
  }
  const headers = Object.keys(rows[0]);
  sheet.addRow(headers);
  rows.forEach((row) => sheet.addRow(headers.map((h) => row[h])));
  sheet.getRow(1).font = { bold: true };
  sheet.columns.forEach((col) => {
    col.width = 18;
  });
  return sheet;
}

function addChartsSheet(workbook, images, metaLines = []) {
  if (!images.length && !metaLines.length) return;

  const sheet = workbook.addWorksheet('Графики');
  sheet.getColumn(1).width = 24;
  let row = 1;

  if (metaLines.length) {
    metaLines.forEach((line) => {
      sheet.getCell(`A${row}`).value = line;
      sheet.getCell(`A${row}`).font = { italic: true, color: { argb: 'FF666666' } };
      row += 1;
    });
    row += 1;
  }

  const maxWidthPx = 720;

  for (const image of images) {
    sheet.getCell(`A${row}`).value = image.title;
    sheet.getCell(`A${row}`).font = { bold: true, size: 13 };
    row += 1;

    const scale = Math.min(1, maxWidthPx / (image.width || maxWidthPx));
    const widthPx = Math.round((image.width || maxWidthPx) * scale);
    const heightPx = Math.round((image.height || 360) * scale);
    const rowSpan = Math.max(12, Math.ceil(heightPx / 18));

    const imageId = workbook.addImage({
      base64: image.base64,
      extension: 'png',
    });

    sheet.addImage(imageId, {
      tl: { col: 0, row: row - 1 },
      ext: { width: widthPx, height: heightPx },
    });

    row += rowSpan + 2;
  }
}

function buildSummaryRows(rows, groupKeys) {
  return rows.map((r) => {
    const byGroup = aggregateByTypeObject(r.by_type);
    const row = {
      Пользователь: r.username,
      'Запросов всего': r.requests_total,
      'Время в сервисе (мин)': Math.round((r.session_sec || 0) / 60),
      'Активное время (мин)': Math.round((r.active_sec || 0) / 60),
      'Пассивное время (мин)': Math.round((r.passive_sec || 0) / 60),
      Входов: r.logins,
      'Первый вход': formatDt(r.first_login),
      'Последний вход': formatDt(r.last_login),
      'Первый запрос': formatDt(r.first_request),
      'Последний запрос': formatDt(r.last_request),
      Ошибок: r.errors,
    };
    for (const gk of groupKeys) {
      row[getRequestGroupLabel(gk)] = byGroup[gk] || 0;
    }
    return row;
  });
}

function buildSessionsRows(sessions) {
  return (sessions || []).map((s) => ({
    Пользователь: s.username,
    Подключение: formatDt(s.connected_at),
    Отключение: formatDt(s.disconnected_at),
    'Длительность (мин)': Math.round((s.duration_sec || 0) / 60),
    IP: s.ip || '—',
  }));
}

function buildDailyRows(dailyRows) {
  return (dailyRows || []).map((d) => ({
    Дата: d.date,
    'Первый вход': formatDt(d.first_login),
    'Последний вход': formatDt(d.last_login),
    'Кол-во входов': d.login_count,
    'Первое подключение': formatDt(d.first_connect),
    'Последнее отключение': formatDt(d.last_disconnect),
    Запросов: d.requests_count,
  }));
}

/**
 * @param {object} chartBlocks — { sessions, requests, distribution } DOM-элементы для снимков
 * @param {string[]} metaLines — подпись периода / фильтров на листе «Графики»
 */
export async function exportUsersWorkSummary(
  rows,
  groupKeys,
  sessions = [],
  dailyRows = [],
  {
    chartBlocks = [],
    metaLines = [],
    filename = buildWorkSummaryFilename(),
  } = {}
) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'CPS Admin';
  workbook.created = new Date();

  addJsonSheet(workbook, 'Сводка', buildSummaryRows(rows, groupKeys));
  addJsonSheet(workbook, 'Сессии', buildSessionsRows(sessions));
  addJsonSheet(workbook, 'По дням', buildDailyRows(dailyRows));

  const blocks = chartBlocks.filter((b) => b?.element);
  if (blocks.length) {
    const images = await captureChartBlocks(blocks);
    if (images.length) {
      addChartsSheet(workbook, images, metaLines);
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBuffer(buffer, filename);

  const sheetCount = workbook.worksheets.length;
  return { sheetCount, chartCount: blocks.length };
}
